<template>
  <div class="upload-component">
    <!-- Error messages -->
    <div v-if="errors.length > 0" class="err">
      <div v-for="error in errors" :key="error">⚠ {{ error }}</div>
    </div>

    <!-- Empty dropzone -->
    <div
      v-if="!selectedFile && !uploadSuccess"
      class="dropzone"
      :class="{ dragging: isDragging || windowDragging, compact: embedded }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <input id="file" ref="file" type="file" @change="onFileChange()" :disabled="isUploading" />
      <label for="file" class="dz-label">
        <div class="dz-icon">↑</div>
        <div class="dz-text">
          <div class="dz-title">
            <template v-if="isDragging || windowDragging">{{ isDragging ? 'Release to drop' : 'Drop it here' }}</template>
            <template v-else>Drag &amp; drop a file, or <span class="dz-browse-inline">browse</span></template>
          </div>
          <div class="dz-sub">{{ embedded ? 'up to 5 GB · auto-deletes when it expires · no account needed' : 'up to 5 GB · auto-deletes on expiry · gets its own short link' }}</div>
        </div>
        <span v-if="embedded" class="dz-browse">Browse</span>
      </label>
    </div>

    <!-- File card (selected / uploading / done) -->
    <div v-else class="file-card lk-card">
      <!-- header -->
      <div class="fc-head">
        <div class="ext">{{ selectedExt }}</div>
        <div class="fc-meta">
          <div class="fc-name">{{ activeName }}</div>
          <div class="fc-size">{{ activeSize }}</div>
        </div>
        <button v-if="selectedFile && !isUploading && !uploadSuccess" class="x-btn" @click="clearSelectedFile">✕</button>
      </div>

      <!-- idle: options + upload -->
      <div v-if="selectedFile && !isUploading && !uploadSuccess" class="fc-body">
        <label class="lk-label">EXPIRES AFTER</label>
        <div class="expiry-row">
          <button
            v-for="(preset, index) in expirationPresets"
            :key="index"
            class="expiry-btn"
            :class="{ active: isSelectedPreset(preset.value) }"
            @click="selectExpirationPreset(preset.value)"
          >{{ preset.label }}</button>
          <button class="expiry-btn" :class="{ active: showCustomDatePicker }" @click="showCustomDatePicker = !showCustomDatePicker">Custom</button>
        </div>

        <div v-if="showCustomDatePicker" class="custom-date-picker">
          <VueDatePicker
            id="expiration-date"
            v-model="expirationDate"
            :disabled="isUploading"
            :default-value="defaultExpirationDate"
            disable-year-select
            auto-apply
            :max-date="maxExpirationDate"
            :min-date="new Date()"
            time-picker
            mobile-friendly
          ></VueDatePicker>
        </div>
        <div v-else class="expiry-msg">Will be deleted in {{ countdownMessage }}</div>

        <button class="lk-btn lk-btn-primary lk-btn-block upload-btn" @click="startUpload">Upload &amp; get link</button>
      </div>

      <!-- uploading -->
      <div v-if="isUploading" class="fc-body">
        <div class="progress-head">
          <span class="progress-label">Uploading…</span>
          <span class="mono progress-pct">{{ uploadProgress.percentage.toFixed(0) }}%</span>
        </div>
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: uploadProgress.percentage + '%' }"></div>
        </div>
        <div class="progress-steps">
          <span><span class="ok">✓</span> Secured</span>
          <span>→ Transferring</span>
          <span>Finishing up</span>
        </div>
        <button class="lk-btn lk-btn-danger lk-btn-block cancel-btn" @click="cancelUpload">Cancel</button>
      </div>

      <!-- done -->
      <transition name="fade">
        <div v-if="uploadSuccess" class="fc-body done">
          <div class="done-ok"><span class="ok-badge">✓</span> File is live &amp; ready to share</div>
          <div class="link-row">
            <span class="mono link-text">{{ fileShortDisplay }}</span>
            <CopyButton :text="fileShortUrl" small />
          </div>
          <div class="done-actions">
            <button class="lk-btn lk-btn-block" @click="goToFileDetails">Open file page</button>
            <button class="lk-btn lk-btn-block" @click="resetUpload">Share another</button>
          </div>
          <div v-if="!embedded" class="redirect-note">
            Opening file page in {{ redirectCountdown }}s…
            <div class="redirect-track"><div class="redirect-fill" :style="{ width: redirectProgress + '%' }"></div></div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import axios from 'axios';
import {mapActions, mapGetters, mapState} from 'vuex';
import CopyButton from '@/components/ui/CopyButton.vue';

export default {
  components: {
    VueDatePicker,
    CopyButton,
  },
  props: {
    // Embedded mode (e.g. inside ShortenTool): the host owns the post-upload
    // flow, so skip the auto-redirect to the file page and emit instead.
    embedded: { type: Boolean, default: false },
  },
  emits: ['uploaded', 'reset'],
  data() {
    return {
      errors: [],
      selectedFile: null,
      lastName: '',
      lastSize: 0,
      isDragging: false,
      // True while a file is being dragged anywhere over the window — lights
      // up the dropzone before the cursor reaches it, and lets a drop landing
      // anywhere on the page still count.
      windowDragging: false,
      winDragDepth: 0,
      isUploading: false,
      uploadSuccess: false,
      cancelTokenSource: null,
      uploadedFileId: null,
      redirectCountdown: 3,
      redirectProgress: 0,
      redirectTimer: null,
      expirationDate: null,
      showCustomDatePicker: false,
      expirationPresets: [
        { label: '1 Hour', value: 1 },
        { label: '24 Hours', value: 24 },
        { label: '3 Days', value: 72 },
        { label: '7 Days', value: 168 }
      ],
      selectedPreset: 1, // Default to 1 hour
    };
  },
  computed: {
    ...mapState('files', ['chunkProgressList']),
    ...mapGetters('files', ['stateUploadProgress']),
    uploadProgress() {
      return this.stateUploadProgress;
    },
    defaultExpirationDate() {
      const now = new Date();
      now.setHours(now.getHours() + 1);
      return now;
    },
    maxExpirationDate() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 23, 59);
    },
    selectedExt() {
      const name = this.selectedFile ? this.selectedFile.name : this.lastName;
      const e = (String(name || '').split('.').pop() || '').toUpperCase();
      return e.length > 4 ? e.slice(0, 4) : (e || '·');
    },
    activeName() {
      return this.selectedFile ? this.selectedFile.name : this.lastName;
    },
    activeSize() {
      const size = this.selectedFile ? this.selectedFile.size : this.lastSize;
      return this.formatFileSize(size || 0);
    },
    fileShortDisplay() {
      if (!this.uploadedFileId) return '';
      return `${window.location.host}/files/${this.uploadedFileId}`;
    },
    fileShortUrl() {
      if (!this.uploadedFileId) return '';
      return `${window.location.origin}/files/${this.uploadedFileId}`;
    },
    countdownMessage() {
      const expirationDiff = this.expirationDate - new Date();
      const days = Math.floor(expirationDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((expirationDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((expirationDiff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours > 1 ? 's' : ''}`;
      } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`;
      } else {
        return `${minutes} minute${minutes > 1 ? 's' : ''}`;
      }
    },
  },
  methods: {
    ...mapActions('files', ['uploadFile']),

    onFileChange() {
      const file = this.$refs.file.files[0];
      this.handleFileSelection(file);
    },

    onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.handleFileSelection(file);
    },

    handleFileSelection(file) {
      this.selectedFile = file;
      this.errors = [];
      this.uploadSuccess = false;
    },

    // ---- Window-wide drag awareness -------------------------------------
    // Depth-counted dragenter/leave (they fire per element) so the highlight
    // doesn't flicker while the cursor crosses child nodes.
    onWinDragEnter(e) {
      if (!e.dataTransfer || ![...(e.dataTransfer.types || [])].includes('Files')) return;
      this.winDragDepth++;
      this.windowDragging = true;
    },
    onWinDragLeave() {
      this.winDragDepth = Math.max(0, this.winDragDepth - 1);
      if (!this.winDragDepth) this.windowDragging = false;
    },
    onWinDragOver(e) {
      // Required so the browser treats the whole page as a drop target
      // instead of navigating to the dropped file.
      if (this.windowDragging) e.preventDefault();
    },
    onWinDrop(e) {
      this.winDragDepth = 0;
      this.windowDragging = false;
      // Already handled by the dropzone's own @drop.prevent — don't double up.
      if (e.defaultPrevented) return;
      e.preventDefault();
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file && !this.isUploading && !this.selectedFile && !this.uploadSuccess) {
        this.handleFileSelection(file);
      }
    },

    clearSelectedFile() {
      this.selectedFile = null;
      // The file input only renders in the empty-dropzone state, so it may be
      // unmounted here — guard the ref.
      if (this.$refs.file) this.$refs.file.value = '';
    },
    
    selectExpirationPreset(hours) {
      this.selectedPreset = hours;
      const date = new Date();
      date.setHours(date.getHours() + hours);
      this.expirationDate = date;
      this.showCustomDatePicker = false;
    },
    
    isSelectedPreset(hours) {
      return this.selectedPreset === hours && !this.showCustomDatePicker;
    },

    // Resolve the expiry at send time. For a preset we recompute now+Nh so it's
    // never stale (the backend enforces a 1h minimum with a small tolerance); for
    // the custom picker we use the chosen absolute date.
    effectiveExpiry() {
      if (this.showCustomDatePicker && this.expirationDate) {
        return new Date(this.expirationDate);
      }
      const d = new Date();
      d.setHours(d.getHours() + this.selectedPreset);
      return d;
    },

    startUpload() {
      this.uploadFile();
    },

    async uploadFile() {
      if (this.selectedFile) {
        this.isUploading = true;
        this.uploadSuccess = false;
        this.showCustomDatePicker = false;
        this.errors = [];
        // Preserve name/size so the "done" state can still show them after
        // selectedFile is cleared below.
        this.lastName = this.selectedFile.name;
        this.lastSize = this.selectedFile.size;

        try {
          const onUploadProgress = (progressEvent, part) => {
            if (part) {
              this.$store.commit('files/setChunkUploadProgress', {
                part: part.PartNumber,
                progress: parseInt((progressEvent.loaded) * 100 / progressEvent.total)
              });
            } else {
              this.$store.commit('files/updateUploadProgress', progressEvent.loaded);
            }
          };

          this.cancelTokenSource = axios.CancelToken.source();

          const fileId = await this.$store.dispatch('files/uploadFile', {
            file: this.selectedFile,
            onUploadProgress,
            expires: this.effectiveExpiry(),
            cancelToken: this.cancelTokenSource.token,
          });

          if (fileId) {
            this.uploadSuccess = true;
            this.uploadedFileId = fileId;
            this.selectedFile = null;
            if (this.$refs.file) this.$refs.file.value = '';

            if (this.embedded) {
              this.$emit('uploaded', { fileId, name: this.lastName });
            } else {
              this.startRedirectCountdown();
            }
          }
        } catch (error) {
          if (axios.isCancel(error)) {
            console.log('Upload canceled');
          } else {
            this.errors.push('An error occurred during file upload.');
            console.error(error);
          }
        } finally {
          this.isUploading = false;
          this.cancelTokenSource = null;
        }
      }
    },

    cancelUpload() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Upload canceled by the user.');
      }
    },

    resetUpload() {
      if (this.redirectTimer) clearInterval(this.redirectTimer);
      this.uploadSuccess = false;
      this.uploadedFileId = null;
      this.selectedFile = null;
      this.lastName = '';
      this.lastSize = 0;
      this.errors = [];
      if (this.$refs.file) this.$refs.file.value = '';
      this.selectExpirationPreset(1);
      this.$emit('reset');
    },

    formatFileSize(size) {
      const units = ['B', 'KB', 'MB', 'GB'];
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index++;
      }
      return `${size.toFixed(2)} ${units[index]}`;
    },

    startRedirectCountdown() {
      this.redirectCountdown = 3;
      this.redirectProgress = 0;
      
      // Set initial timestamp to accurately measure elapsed time
      const startTime = Date.now();
      const duration = 3000; // 3 seconds in milliseconds
      
      this.redirectTimer = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min((elapsedTime / duration) * 100, 100);
        this.redirectProgress = progress;
        
        // Update countdown
        this.redirectCountdown = Math.max(3 - Math.floor(elapsedTime / 1000), 0);
        
        if (elapsedTime >= duration) {
          clearInterval(this.redirectTimer);
          this.goToFileDetails();
        }
      }, 50); // Update every 50ms for smooth animation
    },

    goToFileDetails() {
      if (this.redirectTimer) {
        clearInterval(this.redirectTimer);
      }
      
      if (this.uploadedFileId) {
        this.$router.push({name: 'FileDetails', params: {id: this.uploadedFileId}});
      }
    },
  },

  beforeUnmount() {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }
    window.removeEventListener('dragenter', this.onWinDragEnter);
    window.removeEventListener('dragleave', this.onWinDragLeave);
    window.removeEventListener('dragover', this.onWinDragOver);
    window.removeEventListener('drop', this.onWinDrop);
  },

  mounted() {
    this.expirationDate = this.defaultExpirationDate;
    this.selectExpirationPreset(1); // Default to 1 hour expiration
    window.addEventListener('dragenter', this.onWinDragEnter);
    window.addEventListener('dragleave', this.onWinDragLeave);
    window.addEventListener('dragover', this.onWinDragOver);
    window.addEventListener('drop', this.onWinDrop);
  }
};
</script>

<style scoped>
.upload-component { width: 100%; }

.err {
  background: var(--danger-soft);
  border: 1px solid var(--danger);
  color: var(--danger);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 600;
}

/* Dropzone */
/* Dropzone (mock F3): horizontal strip — dashed hairline, terracotta-soft
   circle, "browse" underlined. */
.dropzone {
  position: relative;
  border: 1.5px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--card);
  padding: 26px;
  cursor: pointer;
  transition: .18s;
}
.dropzone:hover { border-color: var(--accent); }
.dropzone.dragging {
  border-color: var(--accent);
  background: var(--accent-soft);
  box-shadow: 0 0 0 4px var(--ring);
}
.dropzone.dragging .dz-icon { transform: translateY(-2px); }
.dropzone input[type="file"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.dz-label { display: flex; align-items: center; gap: 18px; cursor: pointer; text-align: left; }
.dz-icon {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
  transition: transform .18s ease;
}
.dz-text { flex: 1; min-width: 0; }
.dz-title { font-size: 15px; font-weight: 700; margin-bottom: 3px; color: var(--text); }
.dz-browse-inline { color: var(--accent); border-bottom: 1.5px solid var(--accent); font-weight: 700; }
.dz-sub { font-size: 12.5px; color: var(--faint); }

/* Compact strip (embedded in ShortenTool): one horizontal line that reads as
   a peer of the URL row above it. */
.dropzone.compact { padding: 16px 18px; }
.dropzone.compact .dz-label { display: flex; align-items: center; gap: 14px; text-align: left; }
.dropzone.compact .dz-icon { width: 40px; height: 40px; font-size: 19px; margin: 0; flex-shrink: 0; }
.dropzone.compact .dz-text { flex: 1; min-width: 0; }
.dropzone.compact .dz-title { font-size: 14px; margin-bottom: 2px; }
.dropzone.compact .dz-sub { font-size: 11.5px; color: var(--faint); }
.dz-browse {
  flex-shrink: 0;
  border: 1px solid var(--border-strong);
  background: var(--card);
  color: var(--muted);
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: var(--radius);
  transition: .15s;
}
.dropzone.compact:hover .dz-browse, .dropzone.compact.dragging .dz-browse { color: var(--text); border-color: var(--accent); }

/* File card */
.file-card { overflow: hidden; animation: lk-pop .3s ease both; }
.fc-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border);
}
.ext {
  width: 44px;
  height: 44px;
  border-radius: var(--radius);
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  font-family: var(--font-mono);
  flex-shrink: 0;
}
.fc-meta { flex: 1; min-width: 0; }
.fc-name { font-size: 14.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fc-size { font-size: 12.5px; color: var(--muted); }
.x-btn { border: none; background: transparent; color: var(--faint); font-size: 18px; cursor: pointer; padding: 4px; }
.x-btn:hover { color: var(--text); }

.fc-body { padding: 20px; }

.expiry-row { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
.expiry-btn {
  flex: 1;
  min-width: 70px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
  padding: 10px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: .15s;
}
.expiry-btn.active { background: var(--accent); border-color: var(--accent); color: var(--on-accent); }
.custom-date-picker { margin-bottom: 12px; }
.expiry-msg { font-size: 12.5px; color: var(--muted); margin-bottom: 8px; }
.upload-btn { margin-top: 8px; padding: 13px; }

/* Progress */
.progress-head { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 9px; }
.progress-label { font-weight: 600; color: var(--muted); }
.progress-pct { font-weight: 600; color: var(--accent); }
.progress-track { height: 8px; border-radius: var(--radius); background: var(--surface); overflow: hidden; }
.progress-fill { height: 100%; background: var(--accent); border-radius: var(--radius); transition: width .18s; }
.progress-steps {
  display: flex;
  gap: 16px;
  margin: 16px 0;
  font-size: 12px;
  color: var(--faint);
  flex-wrap: wrap;
}
.progress-steps .ok { color: var(--success); }
.cancel-btn { margin-top: 4px; }

/* Done */
.done-ok {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--success);
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
  justify-content: center;
}
.ok-badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--success);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
.link-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 12px;
}
.link-text { flex: 1; font-size: 14px; font-weight: 600; color: var(--accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.done-actions { display: flex; gap: 9px; }
.redirect-note { margin-top: 16px; font-size: 12px; color: var(--muted); text-align: center; }
.redirect-track { height: 3px; background: var(--surface); border-radius: 2px; overflow: hidden; width: 60%; margin: 8px auto 0; }
.redirect-fill { height: 100%; background: var(--accent); transition: width .05s linear; }

.mono { font-family: var(--font-mono); }

.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>